// Parent sheet (SC-H2, design §2.6). Bottom sheet: status · amount/time stepper · faces
// (child_rating) · note · photo/video · Save. All inputs ≥16px, .hwp- classes (never .hw2-input).
// Faces here are sheet-only; the celebration faces-gate is H4.

import { FACE_SVG } from './faces.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;
const _pad = n => String(n).padStart(2, '0');
const _fmtSec = s => Math.floor(s / 60) + ':' + _pad(s % 60);
const _esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export function openParentSheet(sheetCtx) {
  const { homework, exercise, slot, scheduledDate, presetStatus, mechanicResult, presetRating, existingCompletion, childName, firstName, H, onSave } = sheetCtx;
  const isTimed = !!exercise.duration_seconds;
  const isCounted = !isTimed && !!exercise.reps;
  const sets = exercise.sets || 1;
  const targetSeconds = (exercise.duration_seconds || 0) * sets;
  const targetReps = (exercise.reps || 0) * sets;

  // Prefill precedence (SC-H3):
  //  · finished a mechanic on an ALREADY-DONE completion → ACCUMULATE (existing + this run) [D8]
  //  · finished a mechanic on a fresh entry → this run's value
  //  · re-opening an existing completion (no run) → the saved value (fallback to full target)
  //  · fresh, no run (e.g. "just mark done") → the full target dose
  const doneAlready = existingCompletion?.status === 'done';
  let initSeconds, initValue;
  if (mechanicResult && doneAlready) {
    initSeconds = (existingCompletion.actual_seconds || 0) + (mechanicResult.actual_seconds || 0);
    initValue = (existingCompletion.actual_value || 0) + (mechanicResult.actual_value || 0);
  } else if (mechanicResult) {
    initSeconds = mechanicResult.actual_seconds ?? null;
    initValue = mechanicResult.actual_value ?? null;
  } else if (existingCompletion) {
    initSeconds = existingCompletion.actual_seconds ?? (isTimed ? targetSeconds : null);
    initValue = existingCompletion.actual_value ?? (isCounted ? targetReps : null);
  } else {
    initSeconds = isTimed ? targetSeconds : null;
    initValue = isCounted ? targetReps : null;
  }

  const model = {
    status: presetStatus || existingCompletion?.status || 'done',
    child_rating: presetRating ?? existingCompletion?.child_rating ?? null, // rating carried from the celebration
    actual_value: initValue,
    actual_seconds: initSeconds,
    note: existingCompletion?.note || '',
    photo_path: existingCompletion?.photo_path || null,
    media_type: existingCompletion?.media_type || null,
    _photoFile: null,
  };

  const bg = document.createElement('div'); bg.className = 'hwp-sheet-bg';
  const sheet = document.createElement('div'); sheet.className = 'hwp-sheet';
  const stepLabel = isTimed ? T('hw5_time_done') : T('hw5_units_done');
  sheet.innerHTML =
    `<div class="hwp-sheet-grab"></div>` +
    `<h4 class="ff">${_esc(T('hw5_for_parents'))} · ${_esc(exercise.title || '')}</h4>` +
    `<p class="hwp-sheet-sub">${_esc(T('hw5_only_sees', { name: firstName }))}</p>` +
    `<div class="hwp-row"><label>${_esc(T('hw5_what_happened'))}</label><div class="hwp-seg" id="hwpSeg">` +
      `<button type="button" data-v="done">${_esc(T('hw3_st_done'))}</button>` +
      `<button type="button" class="warn" data-v="skipped">${_esc(T('hw3_st_skipped'))}</button>` +
      `<button type="button" class="warn" data-v="cant_do">${_esc(T('hw3_st_cant'))}</button></div></div>` +
    (isTimed || isCounted
      ? `<div class="hwp-row" id="hwpDoseRow"><label>${_esc(stepLabel)}</label><div class="hwp-stepper">` +
          `<button type="button" id="hwpDec">−</button><b id="hwpDoseV"></b>` +
          (isCounted ? `<small>${_esc(T('hw5_of_n', { n: targetReps }))}</small>` : '') +
          `<button type="button" id="hwpInc">+</button></div></div>`
      : '') +
    `<div class="hwp-row"><label>${_esc(T('hw5_how_child_felt', { child: childName }))}</label>` +
      `<div class="hwp-faces" id="hwpFaces">${[1, 2, 3].map(v => `<button type="button" class="hwp-face${model.child_rating === v ? ' pick' : ''}" data-v="${v}">${FACE_SVG[v]}</button>`).join('')}</div></div>` +
    `<div class="hwp-row"><label>${_esc(T('hw5_note_for', { name: firstName }))}</label>` +
      `<textarea class="hwp-textarea" id="hwpNote" placeholder="${_esc(T('hw5_note_placeholder'))}">${_esc(model.note)}</textarea></div>` +
    `<div class="hwp-row"><button type="button" class="hwp-photo" id="hwpPhoto"></button></div>` +
    `<button type="button" class="hwp-save" id="hwpSave">${_esc(T('hw5_save'))}</button>`;

  document.body.appendChild(bg);
  document.body.appendChild(sheet);
  requestAnimationFrame(() => sheet.classList.add('on'));

  const close = () => { sheet.classList.remove('on'); bg.remove(); setTimeout(() => sheet.remove(), 280); };
  bg.onclick = close;

  // Status segment — toggles the dose row visibility (dose only meaningful for 'done')
  const doseRow = sheet.querySelector('#hwpDoseRow');
  const setStatus = v => {
    model.status = v;
    sheet.querySelectorAll('#hwpSeg button').forEach(b => b.classList.toggle('on', b.dataset.v === v));
    if (doseRow) doseRow.style.display = (v === 'done') ? '' : 'none';
  };
  sheet.querySelectorAll('#hwpSeg button').forEach(b => (b.onclick = () => setStatus(b.dataset.v)));
  setStatus(model.status);

  // Stepper
  if (doseRow) {
    const dv = sheet.querySelector('#hwpDoseV');
    const paint = () => { dv.textContent = isTimed ? _fmtSec(model.actual_seconds || 0) : String(model.actual_value || 0); };
    const step = isTimed ? (exercise.duration_seconds < 60 ? 5 : 60) : 1;
    sheet.querySelector('#hwpDec').onclick = () => { if (isTimed) model.actual_seconds = Math.max(0, (model.actual_seconds || 0) - step); else model.actual_value = Math.max(0, (model.actual_value || 0) - 1); paint(); };
    sheet.querySelector('#hwpInc').onclick = () => { if (isTimed) model.actual_seconds = (model.actual_seconds || 0) + step; else model.actual_value = (model.actual_value || 0) + 1; paint(); };
    paint();
  }

  // Faces
  sheet.querySelectorAll('#hwpFaces .hwp-face').forEach(f => (f.onclick = () => {
    sheet.querySelectorAll('#hwpFaces .hwp-face').forEach(x => x.classList.remove('pick'));
    f.classList.add('pick'); model.child_rating = +f.dataset.v;
  }));

  // Photo / video
  const photoBtn = sheet.querySelector('#hwpPhoto');
  const paintPhoto = () => { photoBtn.textContent = (model._photoFile || model.photo_path) ? '📸 ' + T('hw5_photo_attached') : '📷 ' + T('hw5_add_photo'); };
  paintPhoto();
  photoBtn.onclick = () => {
    const fi = document.createElement('input'); fi.type = 'file'; fi.accept = 'image/*,video/*';
    fi.onchange = ev => { const f = ev.target.files?.[0]; if (!f) return; model._photoFile = f; model.media_type = (f.type || '').startsWith('video') ? 'video' : 'image'; paintPhoto(); };
    fi.click();
  };

  // Save
  const saveBtn = sheet.querySelector('#hwpSave');
  saveBtn.onclick = async () => {
    if (saveBtn.disabled) return;
    saveBtn.disabled = true; saveBtn.textContent = T('hw3_saving');
    try {
      // Upload a newly-picked photo/video first (reuses the existing storage path).
      if (model._photoFile) {
        try {
          const up = await H.SB.uploadFile('homework/' + homework.child_id + '/' + exercise.id, model._photoFile);
          model.photo_path = up?.path || model.photo_path;
        } catch (e) { console.error('❌ hwp photo upload:', e); H.toast?.(T('hw3_photo_fail') || 'Photo failed', 'error'); saveBtn.disabled = false; saveBtn.textContent = T('hw5_save'); return; }
      }
      const doneStatus = model.status === 'done';
      const res = await onSave({
        hwId: homework.id, exId: exercise.id, slot, scheduledDate,
        status: model.status,
        note: sheet.querySelector('#hwpNote').value.trim() || null,
        photoPath: model.photo_path || null,
        mediaType: model.photo_path ? (model.media_type || 'image') : null,
        actualValue: (doneStatus && isCounted) ? (model.actual_value ?? null) : null,
        actualSeconds: (doneStatus && isTimed) ? (model.actual_seconds ?? null) : null,
        childRating: model.child_rating ?? null,
        existingCompletionId: existingCompletion?.id || null,
        previousStatus: existingCompletion?.status || null,
      });
      if (res && res.error) { saveBtn.disabled = false; saveBtn.textContent = T('hw5_save'); return; }
      close();
    } catch (e) {
      console.error('❌ hwp sheet save:', e);
      H.toast?.(T('hw3_save_fail') || 'Could not save', 'error');
      saveBtn.disabled = false; saveBtn.textContent = T('hw5_save');
    }
  };
}
